import React from "react";
import { Season } from "@/types/classTypes";
import { updateSeason, deleteSeason } from "@/services/seasonService";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Edit2, Loader2 } from "lucide-react";
import { useSeasonForm } from "@/hooks/useSeasonForm";
import SeasonFormFields from "./SeasonFormFields";

interface EditSeasonDialogProps {
  season: Season;
  onSeasonUpdated: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSeasonDeleted?: () => void;
}

// Separate content component for the form
const EditSeasonContent: React.FC<{
  season: Season;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => void;
}> = ({ season, onSubmit, onCancel, onDelete }) => {
  const {
    formData,
    isSubmitting,
    handleChange,
    handleSwitchChange,
    handleDateChange,
    saveSeason
  } = useSeasonForm({ 
    season, 
    onUpdated: async (seasonId, updatedData) => {
      await onSubmit(updatedData);
    }
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveSeason();
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <form id="edit-season-form" onSubmit={handleFormSubmit}>
          <SeasonFormFields
            formData={formData}
            handleChange={handleChange}
            handleSwitchChange={handleSwitchChange}
            handleDateChange={handleDateChange}
            season={season}
            onDelete={onDelete ? () => onDelete() : undefined}
          />
        </form>
      </div>
      
      <div className="px-6 py-4 border-t bg-white/80 backdrop-blur-sm flex justify-end gap-3 flex-shrink-0 sticky bottom-0">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          form="edit-season-form" 
          type="submit" 
          disabled={isSubmitting}
          className="bg-orange-600 hover:bg-orange-700"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving Changes...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  );
};

const EditSeasonDialog: React.FC<EditSeasonDialogProps> = ({ 
  season, 
  onSeasonUpdated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onSeasonDeleted
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const handleSubmit = async (data: any) => {
    try {
      const result = await updateSeason(season.id, data);
      
      if (result) {
        handleOpenChange(false);
        onSeasonUpdated();
      }
    } catch (error) {
      console.error("Error updating season:", error);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteSeason(season.id);
      if (result) {
        handleOpenChange(false);
        if (onSeasonDeleted) {
          onSeasonDeleted();
        }
        toast.success('Season deleted successfully');
      }
    } catch (error) {
      console.error("Error deleting season:", error);
      toast.error('Failed to delete season');
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (controlledOnOpenChange) {
      controlledOnOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon"
            className="h-8 w-8"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-orange-50 to-amber-50 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-gray-900">Edit Season</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {season.name} • Manage session dates and settings
          </DialogDescription>
        </DialogHeader>
        
        <EditSeasonContent
          season={season}
          onSubmit={handleSubmit}
          onCancel={() => handleOpenChange(false)}
          onDelete={onSeasonDeleted ? handleDelete : undefined}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditSeasonDialog;
